import { useCallback, useEffect, useState } from 'react';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { fetchCustomerPortalLocations } from './models';
import { PageHeader } from '../../../components/PageHeader';
import { Input } from '../../../components/input/Input';
import { PlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { LinkButton } from '../../../components/button/ButtonLink';
import { Link } from 'react-router-dom';
import { Pagination } from '../../../components/pagination/Pagination';
import { useTranslation } from '../../../contexts/translation-context';
import { ITableHeader, Table } from 'components/table/Table';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { ListPage } from 'components/page-templates/ListPage';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';

export const CustomerPortalLocationsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const { i18n } = useTranslation();
  const { customer } = useCustomerPortalCustomer();
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return fetchCustomerPortalLocations({
        customerId: customer.id,
        cursor: variables.cursor ?? undefined,
        take: variables.take,
        search: searchValue,
      });
    },
    [customer.id, searchValue],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 20,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const tableHeaders: ITableHeader[] = [
    {
      id: 'name',
      name: i18n('customerPortal.locations.name'),
    },
    {
      id: 'address',
      name: i18n('customerPortal.locations.address'),
    },
    {
      id: 'postalCode',
      name: i18n('customerPortal.locations.postalCode'),
    },
    {
      id: 'country',
      name: i18n('customerPortal.locations.country'),
    },
  ];

  useEffect(() => {
    page.reset();
  }, [searchValue]);

  return (
    <>
      <PageHeader title={i18n('customerPortal.locations.locations')} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem={i18n('customerPortal.locations.locations')} />}
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                {i18n('customerPortal.locations.newLocation')}
              </LinkButton>
            }
          />
        }
        filters={
          <Input
            type="text"
            placeholder={i18n('customerPortal.locations.findLocation')}
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(location) => {
            return [
              <Link to={`${location.id}`} className="link-text whitespace-nowrap">
                {location.name}
              </Link>,
              `${location.street} ${location.streetNumber}`,
              `${location.postalCode} ${location.city}`,
              location.country,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
